import React from "react";
import i18n from "@i18n";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { trackEvent } from "@utils/api/tracker";

import {
  isAppBaseRoute,
  isCore,
  isReferralsBuyflow,
  isUS,
  isWinback,
  isMantle,
} from "src/utils/userSegment";
import { isDirectTraffic } from "src/utils/userSegment/features";
import { useTrackEventOnceWhenBottomReached } from "src/hooks/viewport";
import { compassColors } from "src/utils/styling";
import { zIndex } from "src/design-system/styles/zIndices";
import {
  breakpointMobileExtended,
  breakpointMobileOnly,
} from "src/design-system/styles/breakpoints";
import { shouldShowPromoBanner } from "../linear-buyflow/LandingSurveyPage";

const containerStyles = css`
  flex-direction: column;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 25px;
  height: 8vh;
  min-height: 75px;
  z-index: ${zIndex.newContext};

  p:nth-of-type(2) {
    padding-top: 5px;
  }

  @media ${breakpointMobileExtended} {
    padding-top: 0px;
    align-items: center;
  }

  @media ${breakpointMobileOnly} {
    height: auto;
    padding: 5px 25px;
  }
`;

const textStyles = css`
  padding: 0;
  margin: 0;
  font-size: 12px;
  color: #595959;
`;

const linkStyles = css`
  color: ${compassColors.lagoon};
`;

const DisclaimerPlaceholder = styled.div`
  height: 10vh;
  width: 100%;

  @media only screen and (max-height: 600px) {
    height: 16vh;
  }

  @media only screen and (min-height: 601px) and (max-height: 800px) {
    height: 13vh;
  }
`;

export function useShowDisclaimer() {
  const url = new URL(window.location.toString());
  const searchParams = new URLSearchParams(url.search);
  if (__NODE_ENV__ !== "production") {
    // Add a url parameter for debugging purposes.
    const enableGoogle = searchParams.get("enablegoogleorganicdisclaimer");
    if (enableGoogle) {
      return true;
    }
  }
  const googleReferrerRegex =
    /^http(s?):\/\/(?:www\.)?google\.[a-z.]+(?:\/.*)?$/g;
  const isGoogleReferrerAndDirectRoute =
    isDirectTraffic() && googleReferrerRegex.test(document.referrer);

  return (
    !shouldNotShowDisclaimerForReferrer(searchParams) &&
    (isWinback() || isGoogleReferrerAndDirectRoute || isMantle())
  );
}

function shouldNotShowDisclaimerForReferrer(searchParams: URLSearchParams) {
  // We don't want to show the disclaimer for links coming from Pinterest.
  // Pinterest does not allow weight loss language or claims within ads on the platform
  const disallowedReferrer = ["pinterest"];
  for (let i = 0; i < disallowedReferrer.length; i++) {
    if (
      (searchParams.has("sp") &&
        searchParams.get("sp") === disallowedReferrer[i]) ||
      (searchParams.has("utm_source") &&
        searchParams.get("utm_source") === disallowedReferrer[i])
    ) {
      return true;
    }
  }
  return false;
}

export function disclaimerSetBelowFold() {
  return !isMantle() && !window.matchMedia("(max-height: 670px)").matches;
}

export function ResultsVaryDisclaimer() {
  const shouldShowDisclaimer = useShowDisclaimer();
  useTrackEventOnceWhenBottomReached(
    "ScrolledToDisclaimer",
    () =>
      shouldShowDisclaimer &&
      isUS() &&
      (isCore() || isReferralsBuyflow() || isAppBaseRoute())
  );

  return (
    <>
      {disclaimerSetBelowFold() && <DisclaimerPlaceholder />}
      <div css={containerStyles}>
        <p css={textStyles}>
          <span>
            {i18n.t("results-vary-disclaimer:disclaimerUSCore")}&nbsp;
          </span>
          <a
            css={linkStyles}
            href={i18n.t("results-vary-disclaimer:referenceLinkURL")}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("clickedGoogleTrafficDisclaimerLink")}
          >
            {i18n.t("results-vary-disclaimer:referenceLinkText")}
          </a>
        </p>
        {shouldShowPromoBanner() && (
          <p css={textStyles}>
            {i18n.t("results-vary-disclaimer:promoDisclaimer")}
          </p>
        )}
      </div>
    </>
  );
}
